import axios from "./axios";

export async function fetchProjects(controller) {
  return axios({
    method: "get",
    url: "/projects",
    params: controller,
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchProject(id, params = {}) {
  return axios.get(`/projects/` + id, { params });
}

export async function retryProjectTripletexSync(id) {
  const response = await axios.post(`/projects/${id}/retry_tripletex_sync`);
  // sessionExpiredDialog interceptor resolves error.response, so 4xx/5xx
  // still fulfill. Reject those so TripletexSyncWarning shows an error toast.
  if (response.status >= 400) {
    return Promise.reject(response.data ?? { message: response.statusText });
  }
  return response.data;
}

export async function createProject(payload) {
  return axios
    .post(`/projects`, { project: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}
export async function updateProject(payload) {
  return axios
    .put(`/projects/${payload.id}`, { project: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function deleteProject(payload) {
  const project = payload.project;
  return axios
    .delete(`/projects/${project.project_id}`, {
      data: JSON.stringify({
        project,
      }),
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function exportProjectOverviewPdf(projectId) {
  const response = await axios.get(`/projects/${projectId}/overview_pdf`, {
    responseType: "blob",
    headers: { Accept: "application/pdf" },
  });

  if (response.data.type && response.data.type !== "application/pdf") {
    const text = await response.data.text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.errors?.[0] || json.message || "Invalid PDF response");
    } catch (error) {
      if (error.message !== "Invalid PDF response") throw error;
      throw new Error("Response is not a PDF file");
    }
  }

  const arrayBuffer = await response.data.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const pdfHeader = String.fromCharCode(...uint8Array.slice(0, 4));
  if (pdfHeader !== "%PDF") {
    throw new Error("Invalid PDF file");
  }

  const blob = new Blob([arrayBuffer], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `prosjektoversikt_${projectId}_${new Date().toISOString().split("T")[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

// Project "Timer" overview PDF. Rendered by the API (POST /projects/:id/hours_pdf);
// the browser only sends the filtered hour IDs, period and language, and saves the
// returned file. Must stay in sync with Api::V1::Projects::HoursPdfController.
export const PROJECT_HOURS_PDF_MAX_IDS = 5000;

export function projectHoursPdfLanguage(language) {
  const base = String(language || "").toLowerCase().split(/[-_]/)[0];
  if (base === "en" || base === "sv" || base === "pl") return base;
  if (base === "se") return "sv";
  return "nb";
}

function readBlobAsArrayBuffer(blob) {
  if (typeof blob?.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

function readBlobAsText(blob) {
  if (typeof blob?.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

// Blob error bodies carry the JSON problem payload; expose it as response.data so
// normalizeError() sees the API's code/message/recovery.
async function withParsedBlobErrorBody(error) {
  const data = error?.response?.data;
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return error;
  try {
    const parsed = JSON.parse(await readBlobAsText(data));
    error.response = { ...error.response, data: parsed };
  } catch (_parseError) {
    error.response = { ...error.response, data: null };
  }
  return error;
}

function invalidPdfResponseError(status) {
  const error = new Error("Invalid PDF response");
  error.code = "PROJECT_HOURS_PDF_INVALID_RESPONSE";
  error.httpStatus = status ?? null;
  return error;
}

// Keep download names portable: no path separators, reserved or control characters.
export function sanitizeDownloadFilename(name) {
  const cleaned = String(name || "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f<>:"/\\|?*]+/g, "_")
    .replace(/\s+/g, " ")
    .replace(/^[\s.]+|[\s.]+$/g, "")
    .trim();
  return cleaned.slice(0, 180) || null;
}

function filenameFromContentDisposition(header) {
  const value = String(header || "");
  const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try { return sanitizeDownloadFilename(decodeURIComponent(utf8[1].trim())); } catch (_e) { /* fall through */ }
  }
  const plain = value.match(/filename="?([^";]+)"?/i);
  return plain ? sanitizeDownloadFilename(plain[1].trim()) : null;
}

/**
 * Client-side filename when Content-Disposition is missing or not exposed via
 * CORS: `<prefix>_<project number>[_<from>_<to>].pdf`, e.g.
 * `timeoversikt_13002_2026-07-01_2026-07-31.pdf`. Falls back to the project id
 * only when the project has no number.
 */
export function projectHoursPdfFallbackFilename({ prefix, projectNumber, projectId, dateFrom, dateTo } = {}) {
  const parts = [
    prefix || "timeoversikt",
    projectNumber != null && projectNumber !== "" ? projectNumber : projectId,
    dateFrom,
    dateTo,
  ].filter((part) => part != null && String(part).trim() !== "");
  const base = sanitizeDownloadFilename(parts.map((part) => String(part).trim()).join("_").replace(/\s+/g, "-"));
  return `${base || "timeoversikt"}.pdf`;
}

/**
 * Request the server-rendered hours PDF.
 * Resolves { blob, filename } only for HTTP 200 with a body starting with %PDF.
 * Rejects with the axios error (problem payload parsed) or an invalid-response error.
 */
export async function requestProjectHoursPdf(projectId, { hourIds = [], dateFrom = null, dateTo = null, language, fallbackFilename } = {}) {
  const body = {
    hour_ids: hourIds,
    language: projectHoursPdfLanguage(language),
  };
  if (dateFrom) body.date_from = dateFrom;
  if (dateTo) body.date_to = dateTo;

  let response;
  try {
    response = await axios.post(`/projects/${projectId}/hours_pdf`, body, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
  } catch (error) {
    throw await withParsedBlobErrorBody(error);
  }

  // sessionExpiredDialog interceptor resolves error.response, so 4xx/5xx still fulfill.
  if (response?.status >= 400) {
    throw await withParsedBlobErrorBody({ isAxiosError: true, response });
  }

  if (response?.status !== 200 || !response?.data) throw invalidPdfResponseError(response?.status);
  const buffer = await readBlobAsArrayBuffer(response.data);
  const head = String.fromCharCode(...new Uint8Array(buffer).slice(0, 4));
  if (head !== "%PDF") throw invalidPdfResponseError(response.status);

  return {
    blob: new Blob([buffer], { type: "application/pdf" }),
    // Content-Disposition is only readable cross-origin once the API exposes it via CORS.
    filename: filenameFromContentDisposition(response.headers?.["content-disposition"])
      || sanitizeDownloadFilename(fallbackFilename)
      || projectHoursPdfFallbackFilename({ projectId, dateFrom, dateTo }),
  };
}

export function saveBlobAsFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}
